import { useState, useCallback } from "react";
import type {
  AIModeState,
  DiagnosisData,
  LocationData,
  AdjustmentsData,
} from "../types";
import { STEPS } from "../constants";
import { buildQuickSearchDiagnosis } from "../utils/buildQuickSearchDiagnosis";

const LAST_STEP_INDEX = STEPS.length - 1;

const SESSION_KEY = "vision_ai_mode";

const initialState: AIModeState = {
  currentStep: 0,
  completedSteps: [],
  diagnosis: null,
  location: null,
  adjustments: null,
  mode: "full",
};

function loadFromSession(): AIModeState {
  try {
    const stored = sessionStorage.getItem(SESSION_KEY);
    if (stored) {
      return JSON.parse(stored) as AIModeState;
    }
  } catch {
    // ignore parse errors
  }
  return initialState;
}

function saveToSession(state: AIModeState): void {
  try {
    sessionStorage.setItem(SESSION_KEY, JSON.stringify(state));
  } catch {
    // ignore storage errors
  }
}

export function useAIMode() {
  const [state, setState] = useState<AIModeState>(loadFromSession);

  const updateState = useCallback(
    (updater: (prev: AIModeState) => AIModeState) => {
      setState((prev) => {
        const next = updater(prev);
        saveToSession(next);
        return next;
      });
    },
    [],
  );

  const goToStep = useCallback(
    (step: number) => {
      updateState((prev) => ({ ...prev, currentStep: step }));
    },
    [updateState],
  );

  const nextStep = useCallback(() => {
    updateState((prev) => {
      const next = Math.min(prev.currentStep + 1, LAST_STEP_INDEX);
      const completedSteps = prev.completedSteps.includes(prev.currentStep)
        ? prev.completedSteps
        : [...prev.completedSteps, prev.currentStep];
      return { ...prev, currentStep: next, completedSteps };
    });
  }, [updateState]);

  const prevStep = useCallback(() => {
    updateState((prev) => ({
      ...prev,
      currentStep: Math.max(prev.currentStep - 1, 0),
    }));
  }, [updateState]);

  const updateDiagnosis = useCallback(
    (data: DiagnosisData) => {
      updateState((prev) => ({ ...prev, diagnosis: data }));
    },
    [updateState],
  );

  const updateDiagnosisPartial = useCallback(
    (partial: Partial<DiagnosisData>) => {
      updateState((prev) =>
        prev.diagnosis
          ? { ...prev, diagnosis: { ...prev.diagnosis, ...partial } }
          : prev,
      );
    },
    [updateState],
  );

  const updateLocation = useCallback(
    (data: LocationData) => {
      updateState((prev) => ({ ...prev, location: data }));
    },
    [updateState],
  );

  const updateLocationPartial = useCallback(
    (partial: Partial<LocationData>) => {
      updateState((prev) => ({
        ...prev,
        location: prev.location
          ? { ...prev.location, ...partial }
          : ({ ...partial } as LocationData),
      }));
    },
    [updateState],
  );

  const updateAdjustments = useCallback(
    (data: AdjustmentsData) => {
      updateState((prev) => ({ ...prev, adjustments: data }));
    },
    [updateState],
  );

  /**
   * Quick search (PED-12650): jump straight to LocationStep with a stub
   * diagnosis. Deal data is never collected, so nothing from this mode can
   * reach the match-algorithm dataset (import-deal/search-and-rank are only
   * called by the full flow).
   */
  const enterQuickSearch = useCallback(() => {
    updateState((prev) => ({
      ...prev,
      mode: "quick",
      currentStep: 1,
      completedSteps: [],
      diagnosis: buildQuickSearchDiagnosis(),
      location: null,
      adjustments: null,
    }));
  }, [updateState]);

  /** Leave quick search: back to a clean DiagnosisStep in the full flow. */
  const exitQuickSearch = useCallback(() => {
    updateState((prev) => ({
      ...prev,
      mode: "full",
      currentStep: 0,
      completedSteps: [],
      diagnosis: null,
      location: null,
      adjustments: null,
    }));
  }, [updateState]);

  const reset = useCallback(() => {
    sessionStorage.removeItem(SESSION_KEY);
    setState(initialState);
  }, []);

  const initFromProposal = useCallback((proposalState: AIModeState) => {
    const next: AIModeState = {
      ...proposalState,
      currentStep: 0,
      // All steps before the last are already done when editing an existing proposal.
      completedSteps: Array.from({ length: LAST_STEP_INDEX }, (_, i) => i),
    };
    saveToSession(next);
    setState(next);
  }, []);

  return {
    state,
    goToStep,
    nextStep,
    prevStep,
    updateDiagnosis,
    updateDiagnosisPartial,
    updateLocation,
    updateLocationPartial,
    updateAdjustments,
    enterQuickSearch,
    exitQuickSearch,
    reset,
    initFromProposal,
  };
}
